import { useEffect, useRef, useCallback } from "react";
import { API_URL } from "config/axiosInstance";
import { getAccessToken } from "utils/auth";

const DEFAULT_TYPES = [
  "session.connected",
  "session.disconnected",
  "session.reconnecting",
  "session.invalid_number",
  "session.auth_failure",
  "session.qr",
  "validation.failed",
  "queue.updated",
  "message.sent",
  "message.failed",
  "session.recovery",
].join(",");

export function useWhatsAppEvents({ enabled = true, onEvent, types = DEFAULT_TYPES } = {}) {
  const sourceRef = useRef(null);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const disconnect = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      disconnect();
      return undefined;
    }

    const token = getAccessToken();
    if (!token) {
      return undefined;
    }

    const base = API_URL.replace(/\/$/, "");
    const url = `${base}/whatsapp/events/stream?token=${encodeURIComponent(token)}&types=${encodeURIComponent(types)}`;
    const source = new EventSource(url);
    sourceRef.current = source;

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onEventRef.current?.({ type: data.type || "message", payload: data.payload || data, raw: data });
      } catch {
        onEventRef.current?.({ type: "raw", payload: event.data });
      }
    };

    const knownTypes = types.split(",");
    knownTypes.forEach((type) => {
      source.addEventListener(type.trim(), (event) => {
        try {
          const data = JSON.parse(event.data);
          onEventRef.current?.({ type: data.type || type.trim(), payload: data.payload || data, raw: data });
        } catch {
          onEventRef.current?.({ type: type.trim(), payload: event.data });
        }
      });
    });

    source.onerror = () => {
      onEventRef.current?.({ type: "stream.error", payload: { message: "Conexión SSE interrumpida" } });
    };

    return () => {
      source.close();
      sourceRef.current = null;
    };
  }, [enabled, types, disconnect]);

  return { disconnect };
}

export default useWhatsAppEvents;
